import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAccountant } from "@/contexts/AccountantContext";
import { KipaInput, KipaButton, BottomButtonContainer } from "@/components/kipa";

export default function AuthScreen({ initialMode = "login" }) {
  const { login, register } = useAuth();
  const { refresh } = useAccountant();
  const [mode, setMode] = useState(initialMode);
  const [ownerName, setOwnerName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && password !== confirmPassword) {
      setError("Passwords do not match. Please check and try again.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "register") {
        await register(email.trim(), password, ownerName.trim());
      } else {
        await login(email.trim(), password);
      }
      await refresh();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Something went wrong";
      setError(Array.isArray(msg) ? msg[0]?.msg || String(msg) : String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="flex-1 flex flex-col min-h-screen kipa-screen-enter"
      style={{ background: "var(--color-bg)" }}
      data-testid="auth-screen"
    >
      <div className="flex-1 flex flex-col justify-center py-6 max-w-[430px] mx-auto w-full" style={{ paddingLeft: "var(--screen-padding-x)", paddingRight: "var(--screen-padding-x)" }}>
        <div className="text-center mb-5">
          <div className="text-[22px] font-black text-[var(--color-primary)] tracking-tight">KipaBiz</div>
          <div className="text-[13px] text-[var(--color-text-secondary)] mt-0.5">
            Know your numbers. Grow your business.
          </div>
        </div>

        <div className="flex bg-[var(--color-bg-2)] rounded-[var(--radius-lg)] p-0.5 mb-4">
          <button
            type="button"
            className={`flex-1 py-2.5 rounded-xl text-[var(--text-sm)] font-bold kipa-btn-press ${
              mode === "login" ? "bg-white text-[var(--color-primary)] card-sh" : "text-[var(--color-text-secondary)]"
            }`}
            onClick={() => { setMode("login"); setError(""); }}
            data-testid="auth-tab-login"
          >
            Log in
          </button>
          <button
            type="button"
            className={`flex-1 py-2.5 rounded-xl text-[var(--text-sm)] font-bold kipa-btn-press ${
              mode === "register" ? "bg-white text-[var(--color-primary)] card-sh" : "text-[var(--color-text-secondary)]"
            }`}
            onClick={() => { setMode("register"); setError(""); }}
            data-testid="auth-tab-register"
          >
            Create account
          </button>
        </div>

        <form onSubmit={submit} className="space-y-1">
          {mode === "register" && (
            <KipaInput
              label="Your name"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Enter your name"
              autoCapitalize="words"
              testId="auth-owner-name"
              successCondition={(v) => v.trim().length >= 2}
            />
          )}

          <KipaInput
            label="Email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoCapitalize="none"
            testId="auth-email"
            successCondition={(v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())}
          />

          <KipaInput
            label="Password"
            secureTextEntry
            showPasswordToggle
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            testId="auth-password"
            successCondition={(v) => v.length >= 8}
          />

          {mode === "register" && (
            <KipaInput
              label="Confirm password"
              secureTextEntry
              showPasswordToggle
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              testId="auth-confirm-password"
              errorText={
                confirmPassword && password !== confirmPassword
                  ? "Passwords do not match"
                  : undefined
              }
              successCondition={(v) => v.length >= 8 && v === password}
            />
          )}

          {error && (
            <div className="text-[var(--text-sm)] text-[var(--color-error)] font-semibold mb-2">{error}</div>
          )}

          <KipaButton
            type="submit"
            variant="primary"
            fullWidth
            loading={submitting}
            label={mode === "register" ? "Create account" : "Log in"}
            testId="auth-submit"
            className="mt-2"
          />
        </form>
      </div>
    </div>
  );
}
